import React, { useEffect, useRef, useCallback } from "react";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import OSM from "ol/source/OSM";
import XYZ from "ol/source/XYZ";
import Feature from "ol/Feature";
import LineString from "ol/geom/LineString";
import Point from "ol/geom/Point";
import { fromLonLat, transformExtent } from "ol/proj";
import { Stroke, Style, Circle, Fill } from "ol/style";
import { Coordinate } from "ol/coordinate";
import { Position } from "../../types";
import { usePreferences } from "../../contexts/PreferencesContext";
import debugx from "debugx";

const debug = debugx("bikedash:embedded-map");

export interface RouteData {
  id: string;
  positions: Position[];
  timestamps?: number[]; // Optional timestamps for gap detection
  color?: string;
  width?: number;
  type?: "track" | "route";
  gapThreshold?: number; // Time in ms to consider a gap (default 30 seconds)
}

export interface PlaceMarker {
  id: string;
  position: Position;
  color?: string;
  size?: number;
  label?: string;
}

interface EmbeddedMapViewProps {
  routes?: RouteData[];
  places?: PlaceMarker[];
  fitToContent?: boolean;
  interactive?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode; // For overlay content
  onMapReady?: (fitFunction: () => void) => void; // Callback to expose fit function
  onMapMoved?: (isFitted: boolean) => void; // Callback when map is panned/zoomed
  fitPadding?: [number, number, number, number]; // Optional custom padding [top, right, bottom, left]
}

function EmbeddedMapViewComponent({
  routes = [],
  places = [],
  fitToContent = true,
  interactive = true,
  className = "",
  style = {},
  children,
  onMapReady,
  onMapMoved,
  fitPadding = [120, 40, 200, 40], // Default padding for backwards compatibility
}: EmbeddedMapViewProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<Map | null>(null);
  const vectorSourceRef = useRef<VectorSource | null>(null);
  const hasFittedRef = useRef(false);
  const { mapPreferences } = usePreferences();
  const mapPreferencesRef = useRef(mapPreferences);
  const moveTimeoutRef = useRef<number | null>(null);
  const isFittingRef = useRef(false);
  const isFittedRef = useRef(false);
  const isResizingRef = useRef(false);
  const resizeTimeoutRef = useRef<number | null>(null);
  const onMapMovedRef = useRef(onMapMoved);
  const onMapReadyRef = useRef(onMapReady);

  // Update refs when props change
  useEffect(() => {
    onMapMovedRef.current = onMapMoved;
    onMapReadyRef.current = onMapReady;
  }, [onMapMoved, onMapReady]);

  // Update ref when preferences change
  useEffect(() => {
    mapPreferencesRef.current = mapPreferences;
  }, [mapPreferences]);

  // Create fit function with configurable padding
  const fitToExtent = useCallback(() => {
    if (!mapRef.current || !vectorSourceRef.current) return;

    const features = vectorSourceRef.current.getFeatures();
    if (features.length > 0) {
      const extent = vectorSourceRef.current.getExtent();
      // Set fitting flag to ignore movement events during animation
      isFittingRef.current = true;

      mapRef.current.getView().fit(extent, {
        padding: fitPadding,
        maxZoom: 17, // Allow closer zoom
        duration: 300,
      });

      // After animation completes, clear fitting flag and notify fitted
      setTimeout(() => {
        isFittingRef.current = false;
        isFittedRef.current = true;
        if (onMapMoved) {
          onMapMoved(true);
        }
      }, 350); // Wait for animation to complete
    }
  }, [onMapMoved, fitPadding]);

  // Create map once on mount
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Only create map once - don't dispose and recreate

    // Create base layer based on preferences (use ref to avoid re-render)
    const isSatellite =
      mapPreferencesRef.current.defaultStyleId?.includes("satellite") ||
      mapPreferencesRef.current.satelliteUnderlay;

    const tileLayer = new TileLayer({
      source: isSatellite
        ? new XYZ({
            url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
            maxZoom: 19,
          })
        : new OSM(),
    });

    // Create vector source for features
    const vectorSource = new VectorSource();
    vectorSourceRef.current = vectorSource;

    // Create vector layer
    const vectorLayer = new VectorLayer({
      source: vectorSource,
      style: (feature) => {
        const type = feature.get("type");
        const customColor = feature.get("color");
        const customWidth = feature.get("width");

        if (type === "route" || type === "track") {
          // Get default colors from preferences (use ref to avoid re-render)
          const isRoute = type === "route";
          const defaultColor = isRoute
            ? mapPreferencesRef.current.routeTrack?.lineColor || "#9333ea"
            : mapPreferencesRef.current.activityTrack?.lineColor || "#3e6ae1";
          const defaultWidth = isRoute
            ? mapPreferencesRef.current.routeTrack?.lineWidth || 3
            : mapPreferencesRef.current.activityTrack?.lineWidth || 4;

          return new Style({
            stroke: new Stroke({
              color: customColor || defaultColor,
              width: customWidth || defaultWidth,
            }),
          });
        } else if (type === "place") {
          const size = feature.get("size") || 8;
          return new Style({
            image: new Circle({
              radius: size,
              fill: new Fill({
                color: customColor || "#ff4444",
              }),
              stroke: new Stroke({
                color: "#ffffff",
                width: 2,
              }),
            }),
          });
        }
        return undefined;
      },
    });

    // Create map
    const map = new Map({
      target: mapContainerRef.current,
      layers: [tileLayer, vectorLayer],
      view: new View({
        center: fromLonLat([-122.4194, 37.7749]), // San Francisco default
        zoom: 12,
      }),
      controls: [], // No controls for embedded view
      interactions: interactive ? undefined : [],
    });

    mapRef.current = map;

    // Add listener for map movement with debouncing
    if (onMapMoved) {
      const handleMapMove = () => {
        // Ignore movements during fit animation or resize
        if (isFittingRef.current || isResizingRef.current) return;

        // Clear any existing timeout
        if (moveTimeoutRef.current) {
          clearTimeout(moveTimeoutRef.current);
        }
        // Set fitted to false after a short delay to prevent flickering
        moveTimeoutRef.current = window.setTimeout(() => {
          // Double check we're not fitting or resizing
          if (!isFittingRef.current && !isResizingRef.current) {
            debug("Map moved by user, clearing fitted state");
            isFittedRef.current = false;
            if (onMapMovedRef.current) onMapMovedRef.current(false);
          }
        }, 100);
      };

      map.getView().on("change:center", handleMapMove);
      map.getView().on("change:resolution", handleMapMove);
    }

    debug("Embedded map initialized");

    return () => {
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }
      if (mapRef.current) {
        map.dispose();
        mapRef.current = null;
        vectorSourceRef.current = null;
      }
    };
  }, []); // Empty deps - only create map once, ignore fitToExtent and onMapReady

  // Call onMapReady when map is ready
  useEffect(() => {
    if (mapRef.current && onMapReady) {
      onMapReady(fitToExtent);
    }
  }, [onMapReady, fitToExtent]);

  // Add resize observer to re-fit when window resizes if already fitted
  useEffect(() => {
    if (!mapContainerRef.current) return;

    let lastContainerWidth = mapContainerRef.current.offsetWidth;
    let lastContainerHeight = mapContainerRef.current.offsetHeight;

    const handleResize = () => {
      if (!mapContainerRef.current) return;

      const currentWidth = mapContainerRef.current.offsetWidth;
      const currentHeight = mapContainerRef.current.offsetHeight;

      // CHECK IF CONTAINER SIZE ACTUALLY FUCKING CHANGED
      if (
        currentWidth === lastContainerWidth &&
        currentHeight === lastContainerHeight
      ) {
        return; // No change, don't do shit
      }

      debug(
        `Container size changed: ${currentWidth}x${currentHeight}, fitted: ${isFittedRef.current}`,
      );
      lastContainerWidth = currentWidth;
      lastContainerHeight = currentHeight;

      // Update map size immediately
      if (mapRef.current) {
        mapRef.current.updateSize();
      }

      // If fitted, debounce and refit
      if (isFittedRef.current && mapRef.current && vectorSourceRef.current) {
        // Clear existing timeout
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }

        // Set new timeout
        resizeTimeoutRef.current = window.setTimeout(() => {
          debug(
            `EXECUTING REFIT - mapRef: ${!!mapRef.current}, vectorSourceRef: ${!!vectorSourceRef.current}`,
          );
          if (!mapRef.current || !vectorSourceRef.current) {
            debug("ABORT REFIT - refs are null");
            return;
          }

          const extent = vectorSourceRef.current.getExtent();
          debug(`Got extent for refit: ${extent}`);
          isFittingRef.current = true;

          mapRef.current.getView().fit(extent, {
            padding: fitPadding,
            maxZoom: 17,
            duration: 300,
          });
          debug("Called fit() on map view");

          setTimeout(() => {
            isFittingRef.current = false;
            isFittedRef.current = true;
            debug("Refit complete, calling onMapMoved");
            if (onMapMovedRef.current) onMapMovedRef.current(true);
          }, 350);
        }, 300); // Wait 300ms after last resize
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(mapContainerRef.current);

    // Also listen to window resize events as backup
    window.addEventListener("resize", handleResize);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []); // Empty deps - use refs for callbacks

  // Update features when routes/places change
  useEffect(() => {
    if (!mapRef.current || !vectorSourceRef.current) return;

    const vectorSource = vectorSourceRef.current;
    const map = mapRef.current;

    // Clear existing features
    vectorSource.clear();

    // Add routes
    routes.forEach((route) => {
      if (route.positions.length < 2) return;

      const validPositions = route.positions
        .map((pos, idx) => ({ pos, idx }))
        .filter(({ pos }) => pos.lat != null && pos.lng != null);

      if (validPositions.length < 2) return;

      // Track idle, paused, and inactive positions for activity tracks
      const idlePositions: Array<{ coord: Coordinate; idx: number }> = [];
      const pausedPositions: Array<{ coord: Coordinate; idx: number }> = [];
      const inactivePositions: Array<{ coord: Coordinate; idx: number }> = [];

      // If we have timestamps, detect gaps and create separate segments
      if (
        route.timestamps &&
        route.timestamps.length === route.positions.length
      ) {
        const gapThreshold = route.gapThreshold || 10000; // Default 10 seconds
        debug(
          `Processing route ${route.id} with gap threshold: ${gapThreshold}ms`,
        );
        let currentSegment: Coordinate[] = [];
        let segmentIndex = 0;
        let gapsDetected = 0;

        validPositions.forEach(({ pos, idx }, i) => {
          const coord = fromLonLat([pos.lng!, pos.lat!]);

          // Check if this position is idle, paused, or inactive (only for activity tracks)
          if (route.type === "track") {
            if (pos.isIdle) {
              idlePositions.push({ coord, idx });
              debug(`Idle position detected at index ${idx}`);
            }
            if (pos.isPaused) {
              pausedPositions.push({ coord, idx });
              debug(`Paused position detected at index ${idx}`);
            }
            if (pos.isInactive) {
              inactivePositions.push({ coord, idx });
              debug(`Inactive position detected at index ${idx}`);
            }
          }

          if (i === 0) {
            currentSegment = [coord];
          } else {
            const prevIdx = validPositions[i - 1].idx;
            const timeDiff =
              route.timestamps![idx] - route.timestamps![prevIdx];

            if (timeDiff > gapThreshold) {
              gapsDetected++;
              debug(
                `Gap #${gapsDetected}: ${timeDiff}ms (${(timeDiff / 1000).toFixed(1)}s) between points ${prevIdx}->${idx}`,
              );
              // Gap detected - save current segment if it has at least 2 points
              if (currentSegment.length >= 2) {
                const feature = new Feature({
                  geometry: new LineString(currentSegment),
                });
                feature.set("type", route.type || "track");
                feature.set("id", `${route.id}_segment_${segmentIndex++}`);
                if (route.color) feature.set("color", route.color);
                if (route.width) feature.set("width", route.width);
                vectorSource.addFeature(feature);
              }
              // Start new segment
              currentSegment = [coord];
            } else {
              currentSegment.push(coord);
            }
          }
        });

        // Add final segment if it has at least 2 points
        if (currentSegment.length >= 2) {
          const feature = new Feature({
            geometry: new LineString(currentSegment),
          });
          feature.set("type", route.type || "track");
          feature.set("id", `${route.id}_segment_${segmentIndex}`);
          if (route.color) feature.set("color", route.color);
          if (route.width) feature.set("width", route.width);
          vectorSource.addFeature(feature);
        }

        debug(
          `Route ${route.id} processed: ${gapsDetected} gaps detected, ${segmentIndex + 1} segments created`,
        );
      } else {
        // No timestamps, render as single continuous line
        const coordinates = validPositions.map(({ pos }) =>
          fromLonLat([pos.lng!, pos.lat!]),
        );

        const feature = new Feature({
          geometry: new LineString(coordinates),
        });
        feature.set("type", route.type || "track");
        feature.set("id", route.id);
        if (route.color) feature.set("color", route.color);
        if (route.width) feature.set("width", route.width);
        vectorSource.addFeature(feature);
      }

      // Add pause/resume dots for idle positions (only for activity tracks)
      if (route.type === "track" && idlePositions.length > 0) {
        debug(`Adding ${idlePositions.length} idle position markers`);
        idlePositions.forEach(({ coord, idx }) => {
          const idleMarker = new Feature({
            geometry: new Point(coord),
          });
          idleMarker.set("type", "place");
          idleMarker.set("id", `${route.id}_idle_${idx}`);
          idleMarker.set("color", "#9ca3af"); // Gray color for idle
          idleMarker.set("size", 5);
          vectorSource.addFeature(idleMarker);
        });
      }

      // Add dots for explicit pause positions (only for activity tracks)
      if (route.type === "track" && pausedPositions.length > 0) {
        debug(`Adding ${pausedPositions.length} paused position markers`);
        pausedPositions.forEach(({ coord, idx }) => {
          const pausedMarker = new Feature({
            geometry: new Point(coord),
          });
          pausedMarker.set("type", "place");
          pausedMarker.set("id", `${route.id}_paused_${idx}`);
          pausedMarker.set("color", "#fb923c"); // Orange color for explicit pause
          pausedMarker.set("size", 6); // Slightly larger for paused positions
          vectorSource.addFeature(pausedMarker);
        });
      }

      // Add dots for inactive positions (app backgrounded/closed) (only for activity tracks)
      if (route.type === "track" && inactivePositions.length > 0) {
        debug(`Adding ${inactivePositions.length} inactive position markers`);
        inactivePositions.forEach(({ coord, idx }) => {
          const inactiveMarker = new Feature({
            geometry: new Point(coord),
          });
          inactiveMarker.set("type", "place");
          inactiveMarker.set("id", `${route.id}_inactive_${idx}`);
          inactiveMarker.set("color", "#7c3aed"); // Purple color for inactive/background
          inactiveMarker.set("size", 7); // Larger to indicate significant event
          vectorSource.addFeature(inactiveMarker);
        });
      }

      // Add start/end markers for tracks
      if (route.type === "track" && validPositions.length > 0) {
        // Start marker
        const startCoord = fromLonLat([
          validPositions[0].pos.lng!,
          validPositions[0].pos.lat!,
        ]);
        const startMarker = new Feature({
          geometry: new Point(startCoord),
        });
        startMarker.set("type", "place");
        startMarker.set("color", "#00d200");
        startMarker.set("size", 6);
        vectorSource.addFeature(startMarker);

        // End marker (if different from start)
        if (validPositions.length > 1) {
          const lastPos = validPositions[validPositions.length - 1].pos;
          const endCoord = fromLonLat([lastPos.lng!, lastPos.lat!]);
          const endMarker = new Feature({
            geometry: new Point(endCoord),
          });
          endMarker.set("type", "place");
          endMarker.set("color", "#ff4444");
          endMarker.set("size", 6);
          vectorSource.addFeature(endMarker);
        }
      }
    });

    // Add place markers
    places.forEach((place) => {
      if (place.position.lat == null || place.position.lng == null) return;

      const feature = new Feature({
        geometry: new Point(
          fromLonLat([place.position.lng, place.position.lat]),
        ),
      });
      feature.set("type", "place");
      feature.set("id", place.id);
      if (place.color) feature.set("color", place.color);
      if (place.size) feature.set("size", place.size);
      if (place.label) feature.set("label", place.label);

      vectorSource.addFeature(feature);
    });

    // Fit map to content only once when features first load
    if (
      fitToContent &&
      vectorSource.getFeatures().length > 0 &&
      !hasFittedRef.current
    ) {
      const extent = vectorSource.getExtent();
      // Set fitting flag for initial fit
      isFittingRef.current = true;

      map.getView().fit(extent, {
        padding: fitPadding,
        maxZoom: 17, // Allow closer zoom
      });
      hasFittedRef.current = true;

      // After initial fit animation, clear flag and notify
      setTimeout(() => {
        isFittingRef.current = false;
        isFittedRef.current = true;
        if (onMapMoved) {
          onMapMoved(true);
        }
      }, 350); // Wait for animation
    }

    // Reset fitted flag if features are cleared
    if (vectorSource.getFeatures().length === 0) {
      hasFittedRef.current = false;
    }

    debug(
      "Updated map with %d routes and %d places",
      routes.length,
      places.length,
    );
  }, [routes, places]); // Update when routes/places change (removed fitToContent from deps)

  return (
    <div
      className={`embedded-map ${className}`}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        margin: 0,
        padding: 0,
        ...style,
      }}
    >
      <div
        ref={mapContainerRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
        }}
      />
      {children && <>{children}</>}
    </div>
  );
}

// Memoize the component to prevent rerenders when parent updates
export const EmbeddedMapView = React.memo(
  EmbeddedMapViewComponent,
  (prevProps, nextProps) => {
    // Custom comparison - only rerender if routes/places actually change
    // Note: We intentionally DON'T compare onMapReady/onMapMoved callbacks
    // because they may have new references but same behavior
    const routesEqual =
      JSON.stringify(prevProps.routes) === JSON.stringify(nextProps.routes);
    const placesEqual =
      JSON.stringify(prevProps.places) === JSON.stringify(nextProps.places);
    const fitEqual = prevProps.fitToContent === nextProps.fitToContent;
    const interactiveEqual = prevProps.interactive === nextProps.interactive;
    const classEqual = prevProps.className === nextProps.className;
    const styleEqual =
      JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style);
    const childrenEqual = prevProps.children === nextProps.children;

    const shouldSkipRender =
      routesEqual &&
      placesEqual &&
      fitEqual &&
      interactiveEqual &&
      classEqual &&
      styleEqual &&
      childrenEqual;

    if (!shouldSkipRender) {
      debug("Memo comparison failed - rerendering because:", {
        routesEqual,
        placesEqual,
        fitEqual,
        interactiveEqual,
        classEqual,
        styleEqual,
        childrenEqual,
      });
    }

    return shouldSkipRender;
  },
);
